import React, { useMemo, useState, useCallback, useEffect, startTransition } from 'react';
import CampaignInfo from './CampaignInfo';
import { ActiveMissionsList, AvailableMissionsList, MilestonesList } from './MissionsList';
import ConfirmModal from '../ConfirmModal';
import { CameraIcon } from '../../../../Sidebar/icons';
import { DEFAULT_STATUSES } from '../../TaskNode/constants';
import {
  isStatusBlocked,
  isStatusInProgress,
  isStatusNotStarted,
} from '../../utils/statusUtils';
import { useCampaignProgressStats } from '../../../shared/CampaignHeader/hooks/useCampaignProgressStats';
import {
  getStoredAvailableCollapsed,
  saveAvailableCollapsed,
  getStoredActiveCollapsed,
  saveActiveCollapsed,
  getStoredMilestonesCollapsed,
  saveMilestonesCollapsed,
} from './utils/missionsStorage';
import { computeMissionHudLists } from './utils/missionHudLists';
import { computeMilestoneHudEntries } from './utils/milestoneHudProgress';
import { isMilestoneNode } from '../../utils/milestoneUtils';
import './styles/index.css';

/**
 * FlowchartHUD Component
 * 
 * Video game-style HUD overlay showing:
 * - Sprint info (top-left): name + progress
 * - Quest log (bottom-left): Available missions
 * - Milestones (top-right)
 * - Minimap is handled separately (bottom-right)
 */
const FlowchartHUD = ({
  sprintName,
  sprintIcon,
  progress = 0,
  tasks = [],
  statuses = DEFAULT_STATUSES,
  lockStates = {},
  nestedSprints = [],
  allSprints = [],
  isHidden = false,
  onGoToTask,
  onStartTask,
  onCompleteTask,
  // Breadcrumb props for nested campaigns
  breadcrumbPath = [],
  onNavigateToParent,
  onSelectSprint,
  // Rename callback
  onRename,
  // Icon change callback
  onIconChange,
  // Campaign ID for per-campaign persistence
  campaignId = '',
  // Multi-campaign mode
  isMultiCampaignMode = false,
  activeView = null,
  onViewRename = null,
  onViewIconChange = null,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  hideViewExplainerModal = false,
  onHideViewExplainerModalChange,
  // Canvas highlight sync only — list row fill is pure CSS :hover
  // (do not pass hoveredMissionId here or every hover re-renders the HUD)
  onMissionHover,
  onMissionLeave,
}) => {
  // Initialize collapsed states from localStorage per campaign (used for default views)
  const [localAvailableCollapsed, setLocalAvailableCollapsed] = useState(() => 
    getStoredAvailableCollapsed(campaignId)
  );
  const [localActiveCollapsed, setLocalActiveCollapsed] = useState(() => 
    getStoredActiveCollapsed(campaignId)
  );
  const [localMilestonesCollapsed, setLocalMilestonesCollapsed] = useState(() =>
    getStoredMilestonesCollapsed(campaignId)
  );
  const [showViewExplainerModal, setShowViewExplainerModal] = useState(false);

  // Sync collapsed UI state only when switching campaign/view — never wait on
  // the expensive view-save path for expand/collapse feedback.
  useEffect(() => {
    setLocalAvailableCollapsed(
      flowViewVisibility.availableMissionsCollapsed ?? getStoredAvailableCollapsed(campaignId),
    );
    setLocalActiveCollapsed(
      flowViewVisibility.activeMissionsCollapsed ?? getStoredActiveCollapsed(campaignId),
    );
    setLocalMilestonesCollapsed(
      flowViewVisibility.milestonesCollapsed ?? getStoredMilestonesCollapsed(campaignId),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only remount sync
  }, [campaignId, activeView?.id]);

  // Local state is the source of truth for instant expand/collapse
  const isAvailableCollapsed = localAvailableCollapsed;
  const isActiveCollapsed = localActiveCollapsed;
  const isMilestonesCollapsed = localMilestonesCollapsed;

  const persistCollapsed = useCallback((patch) => {
    // Persist in a transition so the UI paints the toggle first
    startTransition(() => {
      if (onFlowViewVisibilityChange) {
        onFlowViewVisibilityChange({ ...flowViewVisibility, ...patch });
      }
    });
  }, [onFlowViewVisibilityChange, flowViewVisibility]);

  const handleToggleAvailable = useCallback(() => {
    const next = !localAvailableCollapsed;
    setLocalAvailableCollapsed(next);
    saveAvailableCollapsed(campaignId, next);
    persistCollapsed({ availableMissionsCollapsed: next });
  }, [localAvailableCollapsed, campaignId, persistCollapsed]);

  const handleToggleActive = useCallback(() => {
    const next = !localActiveCollapsed;
    setLocalActiveCollapsed(next);
    saveActiveCollapsed(campaignId, next);
    persistCollapsed({ activeMissionsCollapsed: next });
  }, [localActiveCollapsed, campaignId, persistCollapsed]);

  const handleToggleMilestones = useCallback(() => {
    const next = !localMilestonesCollapsed;
    setLocalMilestonesCollapsed(next);
    saveMilestonesCollapsed(campaignId, next);
    persistCollapsed({ milestonesCollapsed: next });
  }, [localMilestonesCollapsed, campaignId, persistCollapsed]);

  const { activeTasks, blockedTasks, availableTasks, waitingTasks } = useMemo(
    () => computeMissionHudLists(tasks, statuses, lockStates, nestedSprints, allSprints),
    [tasks, statuses, lockStates, nestedSprints, allSprints],
  );

  const milestoneEntries = useMemo(
    () => computeMilestoneHudEntries(tasks, statuses, nestedSprints, allSprints),
    [tasks, statuses, nestedSprints, allSprints],
  );

  const totalMilestoneCount = useMemo(
    () => (tasks || []).filter(isMilestoneNode).length,
    [tasks],
  );

  const showActiveMissions = flowViewVisibility.activeMissions !== false
    && (activeTasks.length > 0 || blockedTasks.length > 0);
  const showMilestones = flowViewVisibility.milestones !== false;

  // Keep mission status helpers in scope for ESLint/no-undef parity with missionHudLists (no runtime cost).
  void isStatusInProgress;
  void isStatusNotStarted;
  void isStatusBlocked;

  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(tasks, statuses, nestedSprints, allSprints);

  const containerClasses = [
    'flowchart-hud',
    isHidden && 'flowchart-hud--hidden',
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
    >
      {/* Campaign Header - Top Left, matching other views (no glass card) */}
      <CampaignInfo
        sprintName={isMultiCampaignMode ? (activeView?.name || 'Saved View') : sprintName}
        sprintIcon={isMultiCampaignMode ? (activeView?.icon || '👁️') : sprintIcon}
        progress={progress}
        completedCount={completedCount}
        totalCount={totalCount}
        remainingMinutes={remainingMinutes}
        remainingMinMinutes={remainingMinMinutes}
        missingEstCount={missingEstCount}
        remainingBreakdown={remainingBreakdown}
        breadcrumbPath={isMultiCampaignMode ? [] : breadcrumbPath}
        onNavigateToParent={isMultiCampaignMode ? null : onNavigateToParent}
        onSelectSprint={isMultiCampaignMode ? null : onSelectSprint}
        onRename={isMultiCampaignMode ? onViewRename : onRename}
        onIconChange={isMultiCampaignMode ? onViewIconChange : onIconChange}
      />

      {/* Top-left — Active Missions */}
      {showActiveMissions && (
        <div className="flowchart-hud__top-left">
          <div className="flowchart-hud__glass-card flowchart-hud__glass-card--missions flowchart-hud__glass-card--active">
            <ActiveMissionsList
              tasks={activeTasks}
              blockedTasks={blockedTasks}
              isCollapsed={isActiveCollapsed}
              onToggle={handleToggleActive}
              onGoToTask={onGoToTask}
              onCompleteTask={onCompleteTask}
              onMissionHover={onMissionHover}
              onMissionLeave={onMissionLeave}
              isMultiCampaignMode={isMultiCampaignMode}
            />
          </div>
        </div>
      )}

      {/* Top-right — Milestones */}
      {showMilestones && (
        <div className="flowchart-hud__top-right">
          <div className="flowchart-hud__glass-card flowchart-hud__glass-card--missions flowchart-hud__glass-card--milestones">
            <MilestonesList
              entries={milestoneEntries}
              totalMilestoneCount={totalMilestoneCount}
              tasks={tasks}
              statuses={statuses}
              nestedSprints={nestedSprints}
              isCollapsed={isMilestonesCollapsed}
              onToggle={handleToggleMilestones}
              onGoToTask={onGoToTask}
              onMissionHover={onMissionHover}
              onMissionLeave={onMissionLeave}
            />
          </div>
        </div>
      )}

      {/* Quest Log - Bottom Left glass card */}
      {flowViewVisibility.availableMissions !== false && (
        <div className="flowchart-hud__quest-log">
          <div className="flowchart-hud__glass-card flowchart-hud__glass-card--missions flowchart-hud__glass-card--available">
            <AvailableMissionsList
              tasks={availableTasks}
              waitingTasks={waitingTasks}
              activeTasks={activeTasks}
              isCollapsed={isAvailableCollapsed}
              onToggle={handleToggleAvailable}
              onGoToTask={onGoToTask}
              onStartTask={onStartTask}
              onMissionHover={onMissionHover}
              onMissionLeave={onMissionLeave}
              isMultiCampaignMode={isMultiCampaignMode}
            />
          </div>
        </div>
      )}

      {/* Multi-campaign edit warning bar - Bottom center */}
      {isMultiCampaignMode && !hideViewExplainerModal && (
        <div className="flowchart-hud__edit-warning">
          <span className="flowchart-hud__edit-warning-icon">💡</span>
          <span className="flowchart-hud__edit-warning-text">
            Edits here go straight to the source campaigns.
          </span>
          <button 
            className="flowchart-hud__edit-warning-help"
            onClick={() => setShowViewExplainerModal(true)}
          >
            What does this mean?
          </button>
        </div>
      )}

      {/* View Explainer Modal */}
      <ConfirmModal
        isOpen={showViewExplainerModal}
        title="About Saved Views"
        message={`This is a live view — not a copy.

Any changes you make here (moving things, updating statuses, adding or removing missions) happen directly in the original campaigns too.`}
        confirmLabel="Got it"
        cancelLabel={null}
        confirmVariant="primary"
        icon={<CameraIcon size={24} />}
        onConfirm={() => setShowViewExplainerModal(false)}
        onCancel={() => setShowViewExplainerModal(false)}
      >
        <label className="flowchart-hud__dont-show-again">
          <input
            type="checkbox"
            checked={hideViewExplainerModal}
            onChange={(e) => onHideViewExplainerModalChange?.(e.target.checked)}
          />
          <span>Don't show this again</span>
        </label>
      </ConfirmModal>
    </div>
  );
};

// Skip re-renders when only canvas hover state changes upstream
export default React.memo(FlowchartHUD);

